/**
 * Siembra el texto bíblico de arranque (KJV, dominio público) Y el léxico Strong
 * de la CONCORDANCIA en la base PowerSync local, para que el LECTOR y la
 * concordancia funcionen completamente offline desde el primer arranque, sin
 * depender del servidor.
 *
 * Las tablas `bible_verse_content` y `strong_entry` están marcadas como
 * `localOnly` en el esquema (ver packages/powersync/src/schema.ts), así que estas
 * escrituras NO entran en la cola de subida (ps_crud) ni disparan el diálogo de
 * migración.
 *
 * Idempotencia: se persiste un marcador de generación en localStorage. Si la
 * generación sembrada coincide con la actual, no se reinserta. Subir
 * `SEED_GENERATION` fuerza una re-siembra aunque el conteo de filas no cambie
 * (necesario al añadir marcas Strong al texto sin cambiar la cantidad de filas).
 */

import { bible_verse_content, eq, type PowerSyncDrizzleDB, strong_entry } from "@bso/powersync";
import { KJV_SEED_VERSES, SEED_GENERATION, SEED_VERSION } from "../data/kjv-seed";
import { STRONG_LEXICON } from "../data/strong-lexicon";
import { STRONG_TAGGED_VERSES } from "../data/strong-tagged-verses";

const SEED_GENERATION_KEY = "reader-seed-generation";

let seedPromise: Promise<void> | null = null;

function verseId(version: string, book: number, chapter: number, verse: number): string {
  return `${version}-${book}-${chapter}-${verse}`;
}

function coordinateKey(book: number, chapter: number, verse: number): string {
  return `${book}-${chapter}-${verse}`;
}

/** Texto del versículo, con marcas Strong intercaladas si está etiquetado. */
function verseText(book: number, chapter: number, verse: number, fallback: string): string {
  return STRONG_TAGGED_VERSES[coordinateKey(book, chapter, verse)] ?? fallback;
}

/**
 * Inserta el subconjunto de arranque (texto + léxico Strong) si aún no está
 * presente para la generación actual. Resuelve una sola vez por sesión
 * (memoizado) para no reconsultar en cada navegación.
 */
export async function seedReaderContent(db: PowerSyncDrizzleDB | null): Promise<void> {
  if (!db) {
    return;
  }
  if (seedPromise) {
    return seedPromise;
  }

  seedPromise = (async () => {
    try {
      const seededGeneration =
        typeof localStorage === "undefined" ? null : localStorage.getItem(SEED_GENERATION_KEY);
      if (seededGeneration === SEED_GENERATION) {
        return;
      }

      // Las tablas PowerSync son VISTAS sobre ps_data → UPSERT/onConflict no se
      // soporta ("cannot UPSERT a view"). Para una re-siembra limpia (nueva
      // generación) borramos primero y reinsertamos con IDs deterministas.
      await db.delete(bible_verse_content).where(eq(bible_verse_content.version, SEED_VERSION));

      const now = new Date().toISOString();
      // Inserción por lotes para no construir una sentencia gigante de una vez.
      // Secuencial a propósito: mantiene un orden determinista de operaciones
      // sobre la base local (no es trabajo paralelizable).
      const BATCH_SIZE = 100;
      for (let i = 0; i < KJV_SEED_VERSES.length; i += BATCH_SIZE) {
        const batch = KJV_SEED_VERSES.slice(i, i + BATCH_SIZE).map((v) => ({
          id: verseId(SEED_VERSION, v.book, v.chapter, v.verse),
          version: SEED_VERSION,
          book: v.book,
          chapter: v.chapter,
          verse: v.verse,
          text: verseText(v.book, v.chapter, v.verse, v.text),
          created_at: now,
          updated_at: now,
        }));
        // oxlint-disable-next-line no-await-in-loop -- siembra secuencial intencional
        await db.insert(bible_verse_content).values(batch);
      }

      // Léxico Strong (concordancia offline).
      await db.delete(strong_entry);
      for (let i = 0; i < STRONG_LEXICON.length; i += BATCH_SIZE) {
        const batch = STRONG_LEXICON.slice(i, i + BATCH_SIZE).map((e) => ({
          id: e.code,
          code: e.code,
          lemma: e.lemma,
          transliteration: e.transliteration,
          pronunciation: e.pronunciation,
          gloss: e.gloss,
          definition: e.definition,
          language: e.language,
          created_at: now,
          updated_at: now,
        }));
        // oxlint-disable-next-line no-await-in-loop -- siembra secuencial intencional
        await db.insert(strong_entry).values(batch);
      }

      if (typeof localStorage !== "undefined") {
        localStorage.setItem(SEED_GENERATION_KEY, SEED_GENERATION);
      }
    } catch (error) {
      // No bloqueamos el arranque del lector si la siembra falla: el loader cae
      // al API del servidor. Reseteamos para permitir reintento posterior.
      seedPromise = null;
      console.warn("[reader] Fallo al sembrar el texto bíblico/léxico Strong offline", error);
    }
  })();

  return seedPromise;
}
